/**
 * useChatSearch Hook
 *
 * Provides comprehensive search functionality for conversations including:
 * - Quick search through conversation titles
 * - Deep search through all message content
 * - Support for searching equations, formulas, and detailed content
 * - Eager loading of all messages for instant search results
 */

import { useState, useEffect, useMemo } from 'react'
import { getConversationMessages } from '../api/chatApiV2'
import type { Conversation, UIMessage } from '../types'

// =============================================================================
// Types
// =============================================================================

interface SearchStats {
  total: number
  matched: number
  deepSearched: number
}

interface UseChatSearchProps {
  conversations: Conversation[]
  searchQuery: string
  enableDeepSearch?: boolean
}

interface UseChatSearchReturn {
  filteredConversations: Conversation[]
  isSearching: boolean
  searchStats: SearchStats
}

// =============================================================================
// Constants
// =============================================================================

const MIN_QUERY_LENGTH = 2 // Minimum characters for search

// Common stopwords to filter out
const STOPWORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
  'of', 'with', 'by', 'from', 'can', 'u', 'you', 'i', 'is', 'are',
  'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do',
  'does', 'did', 'will', 'would', 'should', 'could', 'may', 'might',
  'must', 'shall', 'this', 'that', 'these', 'those', 'it', 'its',
  'me', 'my', 'we', 'our', 'he', 'she', 'they', 'their', 'what',
  'which', 'who', 'when', 'where', 'why', 'how', 'provide', 'please'
])

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Escape special regex characters
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Check if query is a phrase search (wrapped in quotes)
 */
function isPhraseQuery(query: string): boolean {
  return /^["'].*["']$/.test(query.trim())
}

/**
 * Check if query contains OR operator
 */
function isOrQuery(query: string): boolean {
  return /\s+OR\s+/i.test(query)
}

/**
 * Tokenize query into words, filtering stopwords
 */
function tokenizeQuery(query: string): string[] {
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length >= MIN_QUERY_LENGTH && !STOPWORDS.has(word))
}

/**
 * Create regex for partial word matching (without word boundaries)
 */
function createWordRegex(word: string): RegExp {
  return new RegExp(escapeRegExp(word), 'i')
}

/**
 * Match exact phrase in text
 */
function matchPhrase(text: string, phrase: string): boolean {
  return text.toLowerCase().includes(phrase.toLowerCase())
}

/**
 * Match all words (must all be present, any order)
 */
function matchAllWords(text: string, words: string[]): boolean {
  if (words.length === 0) return true
  const lowerText = text.toLowerCase()
  return words.every((word) => createWordRegex(word).test(lowerText))
}

/**
 * Match any word (at least one must be present)
 */
function matchAnyWord(text: string, words: string[]): boolean {
  if (words.length === 0) return false
  const lowerText = text.toLowerCase()
  return words.some((word) => createWordRegex(word).test(lowerText))
}

/**
 * Main search text matching function
 * Handles phrase search, OR search, and all-words search
 */
function searchText(text: string, query: string): boolean {
  const trimmedQuery = query.trim()

  // Empty or too short query
  if (!trimmedQuery || trimmedQuery.length < MIN_QUERY_LENGTH) {
    return true
  }

  // Phrase search (with quotes)
  if (isPhraseQuery(trimmedQuery)) {
    const phrase = trimmedQuery.replace(/^["']|["']$/g, '')
    return matchPhrase(text, phrase)
  }

  // OR search
  if (isOrQuery(trimmedQuery)) {
    const words = trimmedQuery
      .split(/\s+OR\s+/i)
      .flatMap((part) => tokenizeQuery(part))
    return matchAnyWord(text, words)
  }

  // Default: All words search (AND)
  const words = tokenizeQuery(trimmedQuery)
  if (words.length === 0) return true // All stopwords
  return matchAllWords(text, words)
}

/**
 * Normalize mathematical notation for better searching
 * Handles various formats of equations and special characters
 */
function normalizeMathText(text: string): string {
  return text
    // Remove LaTeX delimiters but keep content
    .replace(/\$\$(.*?)\$\$/g, '$1')
    .replace(/\$(.*?)\$/g, '$1')
    // Normalize whitespace around operators
    .replace(/\s*([+\-*/=<>≤≥≠×÷])\s*/g, ' $1 ')
    // Normalize common math symbols
    .replace(/\\times/g, '×')
    .replace(/\\div/g, '÷')
    .replace(/\\neq/g, '≠')
    .replace(/\\leq/g, '≤')
    .replace(/\\geq/g, '≥')
    .replace(/\\pm/g, '±')
    // Remove LaTeX commands but keep content
    .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1/$2')
    .replace(/\\sqrt\{([^}]+)\}/g, 'sqrt($1)')
    // Normalize multiple spaces
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Extract text from any message part that might contain searchable content
 */
function extractTextFromPart(part: any): string {
  // Guard against undefined/null parts
  if (!part || typeof part !== 'object') {
    return ''
  }

  switch (part.type) {
    case 'text':
    case 'reasoning':
      return part.text || ''
    case 'tool-call':
      // Tool calls might have equation arguments
      return JSON.stringify(part.args || {})
    case 'tool-result':
      // Tool results might contain equation outputs
      return typeof part.result === 'string'
        ? part.result
        : JSON.stringify(part.result || {})
    case 'file':
      // File titles might contain equation references
      return part.title || ''
    case 'source':
      // Source titles might contain equation info
      return part.title || ''
    default:
      return ''
  }
}

/**
 * Extract searchable text from message parts
 * Includes text, reasoning, tool calls, and other parts that may contain equations
 * Applies normalization for better mathematical notation search
 */
function extractSearchableText(messages: UIMessage[]): string {
  // Filter out undefined/null messages and get all parts
  const allParts = messages
    .filter((msg) => msg && msg.parts && Array.isArray(msg.parts))
    .flatMap((msg) => msg.parts)
    .filter((part) => part != null) // Filter out undefined/null parts

  const texts = allParts
    .map((part) => {
      const text = extractTextFromPart(part)
      // Store both original and normalized versions for better matching
      return text ? `${text} ${normalizeMathText(text)}` : ''
    })
    .filter(Boolean)
    .join(' ')

  return texts
}

/**
 * Quick search - searches only in conversation.firstMessage
 */
function quickSearch(
  conversations: Conversation[],
  query: string
): Conversation[] {
  return conversations.filter((conv) => {
    const firstMessage = conv.firstMessage || ''
    const normalizedMessage = normalizeMathText(firstMessage)
    const combinedText = `${firstMessage} ${normalizedMessage}`
    return searchText(combinedText, query)
  })
}

/**
 * Deep search - searches through loaded messages in state
 */
function deepSearch(
  conversations: Conversation[],
  query: string,
  messagesMap: Map<string, UIMessage[]>
): Conversation[] {
  const results = conversations.filter((conv) => {
    // First check title (quick search)
    const firstMessage = conv.firstMessage || ''
    const normalizedFirstMessage = normalizeMathText(firstMessage)
    const combinedTitleText = `${firstMessage} ${normalizedFirstMessage}`

    if (searchText(combinedTitleText, query)) {
      return true
    }

    // Then check loaded messages (deep search)
    const messages = messagesMap.get(conv.id)
    if (messages && messages.length > 0) {
      const searchableText = extractSearchableText(messages)
      return searchText(searchableText, query)
    }

    // No messages loaded yet - exclude from results
    return false
  })

  return results
}

// =============================================================================
// Main Hook
// =============================================================================

export function useChatSearch({
  conversations,
  searchQuery,
  enableDeepSearch = true,
}: UseChatSearchProps): UseChatSearchReturn {
  const [searchStats, setSearchStats] = useState<SearchStats>({
    total: conversations.length,
    matched: 0,
    deepSearched: 0,
  })

  // Store all loaded messages in state (not ref) for proper re-renders
  const [allMessages, setAllMessages] = useState<Map<string, UIMessage[]>>(new Map())

  // Loading state for initial eager load
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)

  // Track which conversations we've loaded
  const [loadedConversations, setLoadedConversations] = useState<Set<string>>(new Set())

  /**
   * Eager loading: Load ALL conversation messages when conversations change
   * This ensures search always has complete data
   */
  useEffect(() => {
    // Only run if deep search is enabled
    if (!enableDeepSearch) return

    // Find conversations that haven't been loaded yet
    const conversationsToLoad = conversations
      .filter((conv) => !loadedConversations.has(conv.id))
      .map((conv) => conv.id)

    // Nothing new to load
    if (conversationsToLoad.length === 0) return

    // Start loading
    setIsLoadingMessages(true)

    // Fetch all messages in parallel
    const fetchPromises = conversationsToLoad.map(async (conversationId) => {
      try {
        const messages = await getConversationMessages(conversationId)
        return { conversationId, messages, success: true }
      } catch (error) {
        console.error(`Failed to load messages for conversation ${conversationId}:`, error)
        return { conversationId, messages: [], success: false }
      }
    })

    // Wait for all fetches to complete
    Promise.all(fetchPromises).then((results) => {
      // Update state with all loaded messages at once
      setAllMessages((prevMessages) => {
        const updated = new Map(prevMessages)
        results.forEach(({ conversationId, messages, success }) => {
          if (success) {
            updated.set(conversationId, messages)
          }
        })
        return updated
      })

      // Mark conversations as loaded
      setLoadedConversations((prev) => {
        const updated = new Set(prev)
        results.forEach(({ conversationId, success }) => {
          if (success) {
            updated.add(conversationId)
          }
        })
        return updated
      })

      // Done loading
      setIsLoadingMessages(false)
    })
  }, [conversations, enableDeepSearch, loadedConversations])

  /**
   * Filter conversations based on search query
   * Now uses state-based messages instead of ref-based cache
   */
  const filteredConversations = useMemo(() => {
    const trimmedQuery = searchQuery.trim()

    // No search query - return all conversations
    if (!trimmedQuery) {
      setSearchStats({
        total: conversations.length,
        matched: conversations.length,
        deepSearched: loadedConversations.size,
      })
      return conversations
    }

    // Perform search (quick or deep based on settings)
    const results = enableDeepSearch
      ? deepSearch(conversations, trimmedQuery, allMessages)
      : quickSearch(conversations, trimmedQuery)

    // Update stats
    setSearchStats({
      total: conversations.length,
      matched: results.length,
      deepSearched: loadedConversations.size,
    })

    return results
  }, [conversations, searchQuery, enableDeepSearch, allMessages, loadedConversations.size])

  return {
    filteredConversations,
    isSearching: isLoadingMessages,
    searchStats,
  }
}

export default useChatSearch
